import { useQuery } from '@tanstack/react-query';
import { ReferenceDataService } from '../ReferenceDataService';

export const useReferenceData = ({ include, exclude } = {}) =>
  useQuery({
    queryKey: ['reference-data', { include, exclude }],
    queryFn: () => ReferenceDataService.getReferenceData({ include, exclude }),
    // The one deliberate exception to the app-wide "no caching" default
    // (src/lib/react-query.js) — locations/fields-of-study/etc. genuinely
    // don't change within a session, so this is the only query hook allowed
    // its own staleTime override. Don't add another one elsewhere without
    // updating that file's own comment.
    staleTime: 60 * 60 * 1000, // 1 hour — reference data is stable
  });

export const useFieldOfStudyMap = () => {
  const { data } = useReferenceData({ include: ['fields_of_study'] });
  const categories = data?.data?.fields_of_study || [];
  const map = {};
  categories.forEach((cat) => {
    (cat.fields || []).forEach((field) => {
      map[field.id] = field.name;
    });
  });
  return map;
};

export const useLanguageMap = () => {
  const { data } = useReferenceData({ include: ['languages'] });
  const items = data?.data?.languages || [];
  return Object.fromEntries(items.map((item) => [item.id, item.name]));
};

export const useLocationMap = () => {
  const { data } = useReferenceData({ include: ['locations'] });
  const items = data?.data?.locations || [];
  return Object.fromEntries(items.map((item) => [item.id, item.name]));
};

export const useStateMap = () => {
  const { data } = useReferenceData({ include: ['states'] });
  const items = data?.data?.states || [];
  return Object.fromEntries(items.map((item) => [item.id, item.name]));
};

// Full reference-skill catalog ({id, name} pairs) — the real taxonomy behind
// candidate/requirement skills, opportunity skill chips, and the Skill
// Resolution "link rows to" pickers.
export const useSkillsList = () => {
  const { data, isLoading } = useReferenceData({ include: ['skills'] });
  return { skills: data?.data?.skills || [], isLoading };
};
